import type { Asset, ImageNode } from '@flobi/artwork-core';
import { SKETCH_FPS, SKETCH_REST_FRAME, resizesInPlace, sketchDensity, sketchFrame, sketchMedia, sketchParams, type SketchSources } from '@flobi/artwork-core/sketch';
import type { SketchPicture } from '@flobi/artwork-core/render';
import { SketchHost, type SketchMediaUpdate } from '@flobi/artwork-core/sketch-host';
import type { CanvasKit, Image as SkImage } from 'canvaskit-wasm';

/**
 * The sketches on a page: each layer's code running in a sandboxed page of its own (SketchHost),
 * and the pictures that come out of it.
 *
 * Every layer has a picture at rest, the one its tiles hold and a still export draws. A layer the
 * canvas wants moving also has a live picture, drawn in a window over the tiles and never through
 * them. Nothing here blocks a frame: the painter is handed the newest picture there is, and a
 * sketch that falls behind costs its own frame rate rather than the canvas's.
 *
 * What a sketch costs is held down in four ways. Only what `want` asks for runs (the canvas asks
 * for the largest few on screen, and for nothing during a gesture). Each sketch has one frame in
 * flight and asks for the next only when it is due, so a slow one drops to the rate it can make;
 * past `SLOW_MS` it is drawn at fewer pixels too. A sketch that says its picture stopped changing
 * is not asked again. And a page nobody has asked for lately is closed, keeping its picture.
 */

/** A frame on the canvas at most this often. */
const LIVE_GAP_MS = 1000 / 24;
/** While its page plays: the sketch's own clock. */
const PLAY_GAP_MS = 1000 / SKETCH_FPS;
/** How recently `want` has spoken for a layer to count as running on the canvas. */
const LIVE_HOLD_MS = 250;
/** Sandboxes open at once. Past it the one wanted least is closed. */
const MOST_OPEN = 6;
/** Two at a time: each one parses p5, and a file of sketches opening must not stall the page. */
const MOST_STARTING = 2;
/** A sandbox nobody has wanted for this long is closed. Its next use starts the sketch over. */
const IDLE_MS = 20_000;
/** A frame slower than this costs the sketch pixels. */
const SLOW_MS = 70;
/** The most pixels of a live frame, far less than a still may have. */
const LIVE_PIXELS = 1_500_000;

/** Pictures at rest kept between visits, by the sketch they are of. */
const REST_CACHE = 'artwork-sketch-rests-v1';
const MOST_RESTS_KEPT = 120;
/** How long a sketch must stay as it is before its picture is kept. */
const KEEP_AFTER_MS = 1_500;

/** A sketch's code, seed, settings and size, as a short key: the same sketch, the same picture. */
async function restKey(signature: string): Promise<string> {
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(signature)));
  return `https://sketch-rest.invalid/${[...digest].map((byte) => byte.toString(16).padStart(2, '0')).join('')}`;
}

/** The p5 build and the libraries: a megabyte nobody downloads until a file has a sketch in it. */
let sources: Promise<SketchSources> | null = null;
export function sketchSources(): Promise<SketchSources> {
  sources ??= Promise.all([
    import('@flobi/artwork-core/sketch/p5.min.js?raw'),
    import('@flobi/artwork-core/sketch/p5.brush.js?raw'),
    import('@flobi/artwork-core/sketch/c2.min.js?raw'),
    import('@flobi/artwork-core/sketch/matter.min.js?raw'),
  ]).then(([p5, brush, c2, Matter]) => ({ p5: p5.default, libraries: { brush: brush.default, c2: c2.default, Matter: Matter.default } }));
  return sources;
}

interface Picture {
  readonly image: SkImage;
  /** A lazy image reads its source again on first use: the bitmap lives as long as it does. */
  readonly bitmap: ImageBitmap;
}

interface Run {
  readonly id: string;
  /** Code, seed, values and size: a change to any starts the sketch again. */
  signature: string;
  width: number;
  height: number;
  code: string;
  seed: number;
  params: Record<string, unknown>;
  /** What each `media` control reads (`p.lumen.media`): the picture of the layer it names, or nothing. */
  pictures: { id: string; picture: SketchPicture | null }[];
  /** The page and the signature `pictures` were last sent for: a new page, or new pictures, sends them again. */
  sentPictures: { host: SketchHost; signature: string } | null;
  host: SketchHost | null;
  starting: boolean;
  /** The picture its tiles hold, kept when its page is closed. */
  rest: Picture | null;
  /** The signature `rest` was drawn for: a sandbox opened again need not make it twice. */
  restOf: string | null;
  live: Picture | null;
  /** The last frame the code has drawn; -1 before its first. */
  drawn: number;
  /** The frame wanted next, or null while nobody is asking. */
  wanted: number | null;
  /** Every frame up to the one wanted must be drawn: a held page's frame is the frame an export has. */
  exact: boolean;
  density: number;
  /** A frame is on its way. Never two. */
  busy: boolean;
  sentAt: number;
  /** A running average of how long a frame takes, in ms. */
  cost: number;
  /** The sketch said its picture no longer changes. */
  still: boolean;
  wantedAt: number;
  liveAt: number;
  failed: string | null;
}

export class SketchFrames {
  /**
   * What a sketch's `shape` control hands it for a value: the outline of the layer it names, in
   * the sketch's canvas pixels (`SketchShapes`). Set once the page's renderer exists.
   */
  shapeOf: ((layerId: string, value: unknown, width: number, height: number) => unknown) | null = null;
  /** What a sketch's `media` control hands it for a value: the image or artboard it names, drawn alone (`SketchPictures`). */
  pictureOf: ((layerId: string, value: unknown) => SketchPicture | null) | null = null;
  /** A picture slot's saved still, by its address (`SketchStills`): what an untouched slot shows. */
  stillOf: ((src: string) => SketchPicture | null) | null = null;
  private readonly runs = new Map<string, Run>();
  private readonly waiting: Run[] = [];
  private starting = 0;
  private flowing = false;
  private disposed = false;

  constructor(
    private readonly ck: CanvasKit,
    /** A layer's picture at rest has changed: its tiles are out of date. */
    private readonly onRest: () => void,
    /** A live frame arrived: draw the canvas, and nothing else. */
    private readonly onFrame: () => void,
  ) {
    // Development only: a bench reads each sketch's cost, density and state from here.
    if (import.meta.env.DEV && typeof window !== 'undefined') (window as { __sketches?: unknown }).__sketches = this.runs;
  }

  /** Whether a page is playing, so a sketch keeps to the clock rather than drawing every frame. */
  setFlowing(flowing: boolean): void {
    this.flowing = flowing;
  }

  /** What the painter draws for this layer: the frame `at` seconds in, its picture at rest when not given. */
  get(asset: Asset, layer: ImageNode & { id: string }, at: number | undefined): SkImage | null {
    const run = this.runFor(asset, layer);
    if (!run) return null;
    if (at === undefined) return run.rest?.image ?? null;
    // Running on the canvas, `want` has already said what is wanted; the window only draws it.
    if (performance.now() - run.liveAt > LIVE_HOLD_MS) {
      run.exact = !this.flowing;
      this.ask(run, sketchFrame(at), null);
    }
    return run.live?.image ?? run.rest?.image ?? null;
  }

  /**
   * The canvas shows this layer running, `wide` device pixels across on screen. With `moving`
   * false it holds still where it is (a gesture is under way) and costs nothing.
   */
  want(asset: Asset, layer: ImageNode & { id: string }, seconds: number, wide: number, moving: boolean): void {
    const run = this.runFor(asset, layer);
    if (!run) return;
    run.liveAt = performance.now();
    run.wantedAt = run.liveAt;
    if (!moving) return;
    run.exact = false;
    this.ask(run, sketchFrame(seconds), wide);
  }

  /**
   * The canvas missed frames while sketches ran: the GPU they share is the bottleneck. Every
   * running sketch gives up pixels and frame rate until the canvas keeps up again.
   */
  strained(): void {
    for (const run of this.runs.values()) {
      if (!run.host || performance.now() - run.liveAt > LIVE_HOLD_MS) continue;
      run.cost = Math.max(run.cost, SLOW_MS) * 1.5;
    }
  }

  /** Whether a layer's picture at rest is ready, and why not if it never will be. */
  rest(layerId: string): SkImage | null {
    return this.runs.get(layerId)?.rest?.image ?? null;
  }

  errorFor(layerId: string): string | null {
    return this.runs.get(layerId)?.failed ?? null;
  }

  /** Closes the pages nobody wants, and forgets the layers that are gone. Called now and then. */
  sweep(alive: (layerId: string) => boolean): void {
    const now = performance.now();
    for (const run of this.runs.values()) {
      if (!alive(run.id)) this.forget(run);
      else if (run.host && !run.busy && now - run.wantedAt > IDLE_MS) this.close(run);
    }
  }

  dispose(): void {
    this.disposed = true;
    for (const run of [...this.runs.values()]) this.forget(run);
  }

  private runFor(asset: Asset, layer: ImageNode & { id: string }): Run | null {
    const sketch = asset.sketch;
    if (!sketch) return null;
    const width = Math.round(asset.width ?? layer.width);
    const height = Math.round(asset.height ?? layer.height);
    const seed = layer.sketch?.seed ?? 1;
    const params = sketchParams(sketch.controls, layer.sketch?.values ?? {}, seed, (value) => this.shapeOf?.(layer.id, value, width, height) ?? null);
    const values = layer.sketch?.values ?? {};
    // A slot nobody has set here shows the still saved with the effect: the layer it showed in Lumens.
    const pictures = sketchMedia(sketch.controls, values).map(({ id, value }) => {
      const still = values[id] === undefined ? sketch.pictures?.[id]?.src : undefined;
      return { id, picture: this.pictureOf?.(layer.id, value) ?? (still ? this.stillOf?.(still) ?? null : null) };
    });
    const signature = JSON.stringify([sketch.code, seed, params, width, height, pictures.map((p) => p.picture?.key ?? null)]);
    let run = this.runs.get(layer.id);
    if (run?.signature === signature) return run;
    if (run) {
      // Set differently: the same page runs it, from the start, and the picture stands until the new one is in.
      Object.assign(run, { signature, width, height, code: sketch.code, seed, params, pictures, sentPictures: null, failed: null, still: false, drawn: -1, wanted: null });
      if (run.host) void this.restart(run);
      else this.enqueue(run);
      return run;
    }
    run = {
      id: layer.id, signature, width, height, code: sketch.code, seed, params, pictures, sentPictures: null, host: null, starting: false, rest: null, restOf: null, live: null,
      drawn: -1, wanted: null, exact: false, density: sketchDensity(width, width, height), busy: false, sentAt: 0, cost: 0, still: false,
      wantedAt: performance.now(), liveAt: 0, failed: null,
    };
    this.runs.set(layer.id, run);
    this.enqueue(run);
    return run;
  }

  private ask(run: Run, frame: number, wide: number | null): void {
    run.wantedAt = performance.now();
    if (run.failed) return;
    if (wide !== null) this.fit(run, wide);
    run.wanted = frame;
    if (!run.host && !run.starting) this.enqueue(run);
    this.pump(run);
  }

  /** The pixels a live frame is drawn at: what the screen shows, no more, and less once it runs slow. */
  private fit(run: Run, wide: number): void {
    if (!resizesInPlace(run.code)) return;
    const budget = Math.sqrt(LIVE_PIXELS / Math.max(1, run.width * run.height));
    // A frame costs what its pixels cost: halving the time takes a quarter fewer on each side, not half.
    const slowed = run.cost > SLOW_MS ? Math.sqrt(SLOW_MS / run.cost) : 1;
    // In steps, so a zoom in progress does not resize the sketch's canvas on every frame.
    run.density = Math.max(0.25, Math.round(Math.min(budget, sketchDensity(wide, run.width, run.height)) * slowed * 4) / 4);
  }

  private enqueue(run: Run): void {
    if (run.starting || this.waiting.includes(run)) return;
    this.waiting.push(run);
    this.startNext();
  }

  private startNext(): void {
    while (this.starting < MOST_STARTING && this.waiting.length > 0) {
      const run = this.waiting.shift()!;
      this.starting++;
      run.starting = true;
      void this.start(run).finally(() => {
        run.starting = false;
        this.starting--;
        this.startNext();
      });
    }
  }

  private async start(run: Run): Promise<void> {
    try {
      // Drawn before, here or in an earlier visit: the picture is back at once, and a sandbox
      // opens only once somebody wants the sketch moving.
      if (run.restOf !== run.signature && await this.restFromCache(run) && run.wanted === null) return;
      const made = await sketchSources();
      if (this.disposed || !this.runs.has(run.id)) return;
      this.makeRoom();
      const host = new SketchHost(made, run.width, run.height, SKETCH_FPS);
      await host.init();
      if (this.disposed || !this.runs.has(run.id)) return host.dispose();
      run.host = host;
      await this.restart(run);
    } catch (error) {
      this.fail(run, error);
    }
  }

  /** From the first frame, up to the picture at rest. */
  private async restart(run: Run): Promise<void> {
    const host = run.host;
    if (!host || run.busy) return;
    run.busy = true;
    const signature = run.signature;
    try {
      await host.load(run.code, run.seed, run.params, run.density);
      run.drawn = -1;
      run.still = false;
      if (run.restOf !== signature) {
        let last: ImageBitmap | null = null;
        for (let frame = 0; frame <= SKETCH_REST_FRAME && run.signature === signature; frame++) {
          const sent = performance.now();
          const bitmap = await host.frame(frame, run.params, run.density, signature, undefined, await mediaOf(run, host));
          run.drawn = frame;
          // A slow sketch is drawn at fewer pixels from its first frame on: its first picture is
          // thirty frames away, and the browser draws it with the same GPU the canvas needs.
          if (frame === 0) {
            run.cost = performance.now() - sent;
            if (run.cost > SLOW_MS && resizesInPlace(run.code)) run.density = Math.max(0.25, Math.floor(run.density * Math.sqrt(SLOW_MS / run.cost) * 4) / 4);
          }
          if (!bitmap) { if (last) { run.still = true; break; } continue; }
          last?.close();
          last = bitmap;
        }
        if (last && run.signature === signature) {
          this.replace(run, 'rest', await this.picture(last));
          run.restOf = signature;
          this.keepRest(run, signature);
          // A frame from before the change is not this sketch any more.
          if (run.live) release(run.live);
          run.live = null;
          this.onRest();
        } else last?.close();
      }
    } catch (error) {
      this.fail(run, error);
    } finally {
      run.busy = false;
    }
    if (run.signature !== signature && !run.failed) await this.restart(run);
    else this.pump(run);
  }

  /** Puts the picture at rest kept for this exact sketch on the layer, if there is one. */
  private async restFromCache(run: Run): Promise<boolean> {
    const signature = run.signature;
    try {
      const kept = await (await caches.open(REST_CACHE)).match(await restKey(signature));
      if (!kept || run.signature !== signature || this.disposed) return false;
      const bitmap = await createImageBitmap(await kept.blob(), { premultiplyAlpha: 'premultiply' });
      this.replace(run, 'rest', { bitmap, image: this.ck.MakeLazyImageFromTextureSource(bitmap, { width: bitmap.width, height: bitmap.height, colorType: this.ck.ColorType.RGBA_8888, alphaType: this.ck.AlphaType.Premul }) });
      run.restOf = signature;
      this.onRest();
      return true;
    } catch {
      // No Cache API (a private window), or the copy is unreadable: the sketch runs, as before.
      return false;
    }
  }

  /**
   * Keeps the picture at rest for the next visit. Only once the layer has held still on it: a
   * slider dragged makes a new sketch on every step, and none of those is worth a file.
   */
  private keepRest(run: Run, signature: string): void {
    setTimeout(() => {
      const rest = run.rest;
      if (this.disposed || run.signature !== signature || run.restOf !== signature || !rest) return;
      void (async () => {
        const canvas = new OffscreenCanvas(rest.bitmap.width, rest.bitmap.height);
        canvas.getContext('2d')!.drawImage(rest.bitmap, 0, 0);
        const blob = await canvas.convertToBlob({ type: 'image/webp', quality: 0.92 });
        const cache = await caches.open(REST_CACHE);
        await cache.put(await restKey(signature), new Response(blob, { headers: { 'content-type': 'image/webp' } }));
        const keys = await cache.keys();
        for (const old of keys.slice(0, Math.max(0, keys.length - MOST_RESTS_KEPT))) await cache.delete(old);
      })().catch(() => undefined);
    }, KEEP_AFTER_MS);
  }

  private pump(run: Run): void {
    if (run.busy || !run.host || run.failed || run.wanted === null || run.still || this.disposed) return;
    const target = run.wanted;
    if (target === run.drawn) return;
    const now = performance.now();
    if (!run.exact && now - run.sentAt < Math.max(this.flowing ? PLAY_GAP_MS : LIVE_GAP_MS, run.cost * 1.2)) return;
    run.busy = true;
    run.sentAt = now;
    void this.step(run, run.host, target, now).finally(() => {
      run.busy = false;
      // Set differently while the frame was out.
      if (run.restOf !== run.signature && run.host && !run.failed) void this.restart(run);
      else if (run.exact) this.pump(run);
    });
  }

  private async step(run: Run, host: SketchHost, target: number, sent: number): Promise<void> {
    try {
      // Back to the start of its time, or round again: a sketch keeps state, so it starts over.
      if (target < run.drawn) {
        await host.load(run.code, run.seed, run.params, run.density);
        run.drawn = -1;
      }
      const next = run.exact ? run.drawn + 1 : target;
      const bitmap = await host.frame(next, run.params, run.density, `${run.signature}|${run.density}`, undefined, await mediaOf(run, host));
      run.drawn = next;
      run.cost = run.cost ? run.cost * 0.8 + (performance.now() - sent) * 0.2 : performance.now() - sent;
      if (!bitmap) {
        if (run.live || run.rest) run.still = true;
        return;
      }
      if (run.exact && next < target) { bitmap.close(); return; }
      this.replace(run, 'live', await this.picture(bitmap));
      this.onFrame();
    } catch (error) {
      this.fail(run, error);
    }
  }

  /** A sandbox's picture is flipped for WebGL's sake; CanvasKit wants it the right way up. */
  private async picture(bitmap: ImageBitmap): Promise<Picture> {
    const upright = await createImageBitmap(bitmap, { imageOrientation: 'flipY' });
    bitmap.close();
    return {
      bitmap: upright,
      image: this.ck.MakeLazyImageFromTextureSource(upright, {
        width: upright.width,
        height: upright.height,
        colorType: this.ck.ColorType.RGBA_8888,
        alphaType: this.ck.AlphaType.Premul,
      }),
    };
  }

  private replace(run: Run, which: 'rest' | 'live', picture: Picture): void {
    if (this.disposed || !this.runs.has(run.id)) return release(picture);
    const old = run[which];
    run[which] = picture;
    if (old) release(old);
  }

  private fail(run: Run, error: unknown): void {
    if (this.disposed || !this.runs.has(run.id)) return;
    run.failed = (error as Error).message;
    console.warn(`A sketch could not run: ${run.failed}`);
    this.close(run);
    this.onRest();
  }

  private makeRoom(): void {
    const open = [...this.runs.values()].filter((run) => run.host).sort((a, b) => a.wantedAt - b.wantedAt);
    for (const run of open.slice(0, Math.max(0, open.length - MOST_OPEN + 1))) if (!run.busy) this.close(run);
  }

  private close(run: Run): void {
    run.host?.dispose();
    run.host = null;
    run.drawn = -1;
    run.wanted = null;
    run.still = false;
  }

  private forget(run: Run): void {
    this.close(run);
    if (run.rest) release(run.rest);
    if (run.live) release(run.live);
    this.runs.delete(run.id);
  }
}

/**
 * Every picture a sketch's `media` controls read, as the page takes them, when this page has not
 * been sent them yet. The page keeps them for every frame after, and through every load.
 */
async function mediaOf(run: Run, host: SketchHost): Promise<SketchMediaUpdate | undefined> {
  if (!run.pictures.length || (run.sentPictures?.host === host && run.sentPictures.signature === run.signature)) return undefined;
  run.sentPictures = { host, signature: run.signature };
  const set = await Promise.all(run.pictures.map(async ({ id, picture }) => ({
    id,
    bitmap: picture ? await createImageBitmap(new ImageData(new Uint8ClampedArray(picture.pixels), picture.width, picture.height)) : null,
  })));
  return { set };
}

function release(picture: Picture): void {
  picture.image.delete();
  picture.bitmap.close();
}
